import React from 'react';
import { FeaturedIcon, PaidIcon, MembersIcon } from '../icons';
import { ResponsiveStyles, TILE_GRID_CSS } from './responsive_styles';

// User-facing copy for any search failure. Raw error detail (status codes,
// backend URLs, transport messages) must never reach the DOM; it is logged to
// the console in use_search and sent to the client-error beacon.
export const SEARCH_ERROR_MESSAGE = 'Search is temporarily unavailable. Please try again.';

// ResultTitle renders the highlighted title plus visibility/featured icons.
// Shared by the list and tile variants -- which is why showAccessIcons is
// threaded through it rather than through either call site: a toggle honoured
// in the list and ignored in tiles is the #16236/#16237/#16278 defect class
// exactly, a value that exists, looks wired, and works in one place.
function ResultTitle({ result, showAccessIcons }) {
  return (
    <h3 className="vo-search-search-result-title">
      {parseHighlightedAttribute(
        '<span class="vo-search-highlighted">',
        '</span>',
        result._formatted?.title || result.title
      ).map((title, i) => (
        <span
          key={`title-${i}`}
          className={title.isHighlighted ? 'vo-search-highlighted' : ''}
        >
          {title.value}
        </span>
      ))}
      {showAccessIcons && getResultIcons(result)}
    </h3>
  );
}

// ResultExcerpt renders the highlighted excerpt snippet, or nothing when
// disabled/absent. showExcerpts is the public-config key of the same name
// (#16236); it defaults true so instances that never set it are unchanged.
function ResultExcerpt({ result, showExcerpts }) {
  if (!showExcerpts) return null;
  if (!(result._formatted?.excerpt || result.excerpt)) return null;
  return (
    <p className="vo-search-search-result-excerpt">
      {parseHighlightedAttribute(
        '<span class="vo-search-highlighted">',
        '</span>',
        result._formatted?.excerpt || result.excerpt
      ).map((excerpt, i) => (
        <span
          key={`excerpt-${i}`}
          className={excerpt.isHighlighted ? 'vo-search-highlighted' : ''}
        >
          {excerpt.value}
        </span>
      ))}
    </p>
  );
}

// ResultTags renders the public tags as pills, or nothing when disabled/empty.
function ResultTags({ result, primaryColor, displayTags }) {
  if (!displayTags || !result.tags || result.tags.length === 0) return null;
  const publicTags = result.tags.filter((tag) => tag.visibility === 'public');
  if (publicTags.length === 0) return null;
  return (
    <div className="vo-search-search-result-tags" style={{ marginTop: '8px' }}>
      {publicTags.map((tag, i) => (
        <span
          key={`tag-${tag.id || i}`}
          className="vo-search-search-result-tag"
          style={{
            display: 'inline-block',
            background: `${primaryColor}20`,
            color: primaryColor,
            border: `1px solid ${primaryColor}40`,
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '0.8rem',
            marginRight: '6px',
            marginBottom: '6px'
          }}
        >
          {parseHighlightedAttribute(
            '<span class="vo-search-highlighted">',
            '</span>',
            tag._formatted?.name || tag.name
          ).map((name, j) => (
            <span
              key={`tag-text-${j}`}
              className={name.isHighlighted ? 'vo-search-highlighted' : ''}
              // The weight is a local decision -- a highlighted tag name is
              // bolder than the block's 500 -- and stays. The COLOUR is not:
              // inline, it beat the .vo-search-highlighted rule here exactly as
              // it did in the title and the excerpt (item #17164).
              style={name.isHighlighted ? { fontWeight: 'bold' } : {}}
            >
              {name.value}
            </span>
          ))}
        </span>
      ))}
    </div>
  );
}

const DEFAULT_EXCERPT_LINES = 2;
const MAX_EXCERPT_LINES = 4;

/**
 * Normalises an instance's excerpt line count to the 1-4 range the server
 * clamps to (i22827), so a hand-edited embed cannot break the layout.
 *
 * @param {number|string|null|undefined} value - Raw excerptLines from config.
 * @returns {number} Whole lines between 1 and 4; unset or non-positive is 2.
 */
export function clampExcerptLines(value) {
  const lines = Math.floor(Number(value));
  if (!Number.isFinite(lines) || lines <= 0) return DEFAULT_EXCERPT_LINES;
  return Math.min(lines, MAX_EXCERPT_LINES);
}

const THUMB_BASE_PX = 56;
const THUMB_PER_EXCERPT_LINE_PX = 20;
const THUMB_TAGS_PX = 24;
const THUMB_MAX_PX = 120;

/**
 * Width of the list thumbnail column, from the rows the instance renders
 * (i22831): the title row, each excerpt line when excerpts are on, and the
 * tag row when tags are on, clamped to 56-120px.
 *
 * @param {Object} options
 * @param {boolean} options.showExcerpts - Excerpts are rendered.
 * @param {number} options.excerptLines - Excerpt line count, clamped 1-4.
 * @param {boolean} options.displayTags - The tag row is rendered.
 * @returns {number} Column width in CSS px.
 */
export function listThumbnailWidth({ showExcerpts, excerptLines, displayTags }) {
  const excerptPx = showExcerpts ? THUMB_PER_EXCERPT_LINE_PX * clampExcerptLines(excerptLines) : 0;
  const tagsPx = displayTags ? THUMB_TAGS_PX : 0;
  return Math.min(THUMB_BASE_PX + excerptPx + tagsPx, THUMB_MAX_PX);
}

/**
 * The list row's leading thumbnail: the feature image, or a decorative
 * placeholder that holds the column for a result without one (i22828).
 *
 * @param {Object} props
 * @param {string} [props.src] - The result's feature_image URL.
 */
function ListThumbnail({ src }) {
  if (!src) {
    return <span className="vo-search-result-thumbnail is-empty" aria-hidden="true" />;
  }
  return <img className="vo-search-result-thumbnail" src={src} alt="" loading="lazy" />;
}

export const SearchResults = ({
  results,
  error,
  onResultClick,
  primaryColor,
  isLoading,
  displayTags = true,
  variant = 'list',
  showHeroImage = true,
  showExcerpts = true,
  showThumbnails = true,
  // The featured / paid / members glyphs (item #17161). Default on, so no live
  // instance moves. A toggle rather than a deletion: whether a subscription
  // publisher wants its paywall signal in search is a product call, and the
  // toggle keeps the answer that instance's and keeps it reversible.
  showAccessIcons = true,
  openInNewTab = false,
  tileColumns = 3,
  // Lines of excerpt before the clamp (i22827), read by the excerpt rule as
  // --vo-excerpt-lines on the results container.
  excerptLines = DEFAULT_EXCERPT_LINES
}) => {
  if (error) {
    return (
      <div className="vo-search-error-message" role="alert">
        {SEARCH_ERROR_MESSAGE}
      </div>
    );
  }

  // Show loading state while a request is in flight and we have nothing to show
  // yet, so the widget never renders a blank body mid-query.
  if (isLoading && !results?.hits?.length) {
    return (
      <div className="vo-search-loading-results" role="status" aria-live="polite" aria-busy="true">
        <div className="vo-search-loading-spinner" aria-hidden="true"></div>
        <p>Searching...</p>
      </div>
    );
  }

  // Empty (or absent) result set: show a message instead of crashing on
  // results.hits.map or rendering an empty container.
  if (!results?.hits?.length) {
    return (
      <div className="vo-search-no-results">
        <p>No results found</p>
      </div>
    );
  }

  const handleClick = (result, index) => (e) => {
    e.preventDefault();
    // Pass 1-indexed position within current page
    onResultClick(result, index + 1);
  };

  // openInNewTab (#16278) is the public-config key behind the dashboard's
  // "Open results in new tab" toggle. Defaults off, so an instance whose config
  // predates the key renders exactly what it rendered before.
  //
  // These attributes do NOT decide where a left click lands: the card wrapper
  // above calls preventDefault(), so the anchor's own behaviour never runs and
  // onResultClick owns the navigation. They are here for the paths the handler
  // does not see -- middle click, ctrl/cmd click, "open link in new tab" from
  // the context menu -- and so the markup states the intent rather than hiding
  // it in a click handler.
  //
  // rel is not optional alongside target=_blank: without noopener the opened
  // page gets a window.opener handle back into the customer's page, and
  // noreferrer keeps our URL out of their referrer.
  const excerptLinesVar = { '--vo-excerpt-lines': clampExcerptLines(excerptLines) };

  const linkTargetAttrs = openInNewTab
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  // Tile variant: image-forward responsive card grid (#5414). Columns come from
  // tileColumns; responsive collapse is handled in CSS (item #5429). The hero is
  // the only thumbnail SearchResults renders, so showThumbnails (#16237) gates
  // it alongside the pre-existing showHeroImage; either one off hides it.
  if (variant === 'tile') {
    return (
      <>
        <ResponsiveStyles css={TILE_GRID_CSS} />
        <div className="vo-search-tile-grid" style={{ '--vo-tile-columns': tileColumns, ...excerptLinesVar }}>
        {results.hits.map((result, index) => (
          <div
            key={result.id || result.slug}
            className="vo-search-tile-card"
            data-result-id={result.id || result.slug}
            onClick={handleClick(result, index)}
          >
            <a href={result.url} className="vo-search-tile-card-link" {...linkTargetAttrs}>
              {showHeroImage && showThumbnails && result.feature_image && (
                <div className="vo-search-tile-hero">
                  <img
                    className="vo-search-tile-hero-img"
                    src={result.feature_image}
                    alt=""
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              )}
              <div className="vo-search-tile-body">
                <ResultTitle result={result} showAccessIcons={showAccessIcons} />
                <ResultExcerpt result={result} showExcerpts={showExcerpts} />
                <ResultTags result={result} primaryColor={primaryColor} displayTags={displayTags} />
              </div>
            </a>
          </div>
        ))}
        </div>
      </>
    );
  }

  // i22828: when thumbnails are on and any hit on this page has an image,
  // every row keeps the thumbnail column so mixed lists share one left edge.
  const reserveThumb = showThumbnails && results.hits.some((hit) => hit.feature_image);

  return (
    <>
      <div
        className="vo-search-search-results"
        style={{
          ...excerptLinesVar,
          '--vo-result-thumb-width': `${listThumbnailWidth({ showExcerpts, excerptLines, displayTags })}px`,
        }}
      >
      {results.hits.map((result, index) => {
        const text = (
          <>
            <ResultTitle result={result} showAccessIcons={showAccessIcons} />
            <ResultExcerpt result={result} showExcerpts={showExcerpts} />
            <ResultTags result={result} primaryColor={primaryColor} displayTags={displayTags} />
          </>
        );
        return (
          <div
            key={result.id || result.slug}
            className="vo-search-search-result"
            data-result-id={result.id || result.slug}
            onClick={handleClick(result, index)}
          >
            <a
              href={result.url}
              className={reserveThumb ? 'vo-search-search-result-link has-thumb' : 'vo-search-search-result-link'}
              {...linkTargetAttrs}
            >
              {reserveThumb ? (
                <>
                  <ListThumbnail src={result.feature_image} />
                  <div className="vo-search-search-result-text">{text}</div>
                </>
              ) : text}
            </a>
          </div>
        );
      })}
      </div>
    </>
  );
};

function parseHighlightedAttribute(preTag, postTag, highlightedValue = '') {
  if (!highlightedValue) return [{ value: '', isHighlighted: false }];

  // Replace all instances of highlighted text markers with our own markers
  const text = highlightedValue.replace(/<span class="highlighted-text">/g, preTag).replace(/<\/span>/g, postTag);

  const result = [];
  let remaining = text;
  let index = remaining.indexOf(preTag);

  while (index !== -1) {
    // Add non-highlighted text
    if (index > 0) {
      result.push({
        value: remaining.substring(0, index),
        isHighlighted: false
      });
    }

    // Extract highlighted part
    const endIndex = remaining.indexOf(postTag, index + preTag.length);
    if (endIndex === -1) break;

    result.push({
      value: remaining.substring(index + preTag.length, endIndex),
      isHighlighted: true
    });

    // Update remaining text and find next highlight
    remaining = remaining.substring(endIndex + postTag.length);
    index = remaining.indexOf(preTag);
  }

  // Add any remaining non-highlighted text
  if (remaining) {
    result.push({
      value: remaining,
      isHighlighted: false
    });
  }

  return result;
}

function getResultIcons(result) {
  const icons = [];

  if (result.featured) {
    icons.push(
      <span key="featured" className="vo-search-result-icon vo-search-featured-icon" title="Featured">
        <FeaturedIcon />
      </span>
    );
  }

  if (result.visibility === 'paid') {
    icons.push(
      <span key="paid" className="vo-search-result-icon vo-search-locked-icon" title="Paid-Members Only">
        <PaidIcon />
      </span>
    );
  } else if (result.visibility === 'members') {
    icons.push(
      <span key="members" className="vo-search-result-icon vo-search-members-icon" title="Members Only">
        <MembersIcon />
      </span>
    );
  }

  return icons.length ? <span className="vo-search-result-icons">{icons}</span> : null;
}
